// src/pages/DocsPage.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

// =================== styles ===================
const shimmer = keyframes`
  0% { background-position: 0% 50% }
  100% { background-position: 200% 50% }
`;

const Page = styled.section`
  padding: 32px 0 72px;
  .kicker { font-size: 13px; letter-spacing: .12em; text-transform: uppercase; opacity:.7; }
  .lead { color: ${({theme})=>theme.textMuted}; margin: 6px 0 0; }
`;

const Container = styled.div`
  width: ${({theme})=>theme.container};
  margin: 0 auto;
`;

const Layout = styled.div`
  display: grid; grid-template-columns: 280px 1fr; gap: 20px; align-items: start;
  @media (max-width: 1100px){ grid-template-columns: 1fr; }
`;

const StickyNav = styled.aside`
  position: sticky; top: 90px;
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  box-shadow:${({theme})=>theme.shadow.sm};
  padding: 12px;
  @media (max-width: 1100px){ position: static; }
`;

const NavSearch = styled.div`
  display:flex; gap:8px; margin-bottom: 10px;
  input{
    width:100%; padding:10px 12px; border-radius:${({theme})=>theme.radius.md}px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body}; color:inherit;
  }
`;

const NavList = styled.ul`
  list-style:none; margin:0; padding:0; display:grid; gap:6px;
  li a{
    display:flex; align-items:center; gap:8px;
    padding:8px 10px; border-radius:${({theme})=>theme.radius.md}px;
    text-decoration:none; color:${({theme})=>theme.text};
    border:1px solid transparent;
  }
  li a.active{
    border-color:${({theme})=>theme.border};
    background:${({theme})=>theme.body};
  }
`;

const Main = styled.div`
  display:grid; gap: 32px;
`;

const SectionHead = styled.div`
  display:flex; align-items:flex-end; justify-content:space-between; gap:12px; margin-bottom: 10px;
`;

const DocGrid = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 12px;
  @media (max-width: 1180px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media (max-width: 680px){ grid-template-columns: 1fr; }
`;

const Card = styled.article`
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  box-shadow:${({theme})=>theme.shadow.sm};
  display:flex; gap:12px; padding:12px; position:relative; overflow:hidden;

  .stripe{
    position:absolute; inset:0;
    background:
      linear-gradient(120deg,#ffffff10,#ffffff00 40%) padding-box,
      linear-gradient(90deg,#ff660055,#00c2ff55,#a8ff0055) border-box;
    background-size: 200% 100%;
    animation: ${shimmer} 6s linear infinite;
    opacity:.05; pointer-events:none;
  }

  .icon{
    flex: 0 0 44px; height:44px; border-radius:12px; display:grid; place-items:center;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
  }
  .meta{
    display:flex; flex-direction:column; gap:6px; min-width:0;
  }
  h3{
    margin:0; font-size:16px; line-height:1.25; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }
  .badges{ display:flex; flex-wrap:wrap; gap:6px; }
  .badge{
    font-size:12px; padding:4px 8px; border-radius:${({theme})=>theme.radius.pill}px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
  }
  p{ margin:0; color:${({theme})=>theme.textMuted}; font-size:13px; }
  .actions{ margin-top:auto; display:flex; gap:8px; flex-wrap:wrap; }
  a.btn{
    text-decoration:none; font-weight:600; font-size:13px;
    padding:8px 10px; border-radius:${({theme})=>theme.radius.md}px;
    border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface}; color:${({theme})=>theme.text};
  }
  a.btn--primary{
    background: ${({theme})=>theme.accent}; color: #111; border-color: transparent;
  }
`;

const InfoNote = styled.div`
  padding:12px; border:1px dashed ${({theme})=>theme.border};
  background:${({theme})=>theme.body}; border-radius:${({theme})=>theme.radius.lg}px;
  color:${({theme})=>theme.textMuted};
`;

const Block = styled.section`
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  box-shadow:${({theme})=>theme.shadow.sm};
  padding:16px;
`;

const Accordion = styled.div`
  border:1px solid ${({theme})=>theme.border};
  border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden;
  .q{
    width:100%; padding:12px 14px; display:flex; align-items:center; justify-content:space-between; gap:12px;
    background:${({theme})=>theme.surface}; border:none; text-align:left; cursor:pointer; font-weight:700;
  }
  .a{ padding: 0 14px 12px; color:${({theme})=>theme.textMuted}; }
`;

// =================== helpers & data ===================
const formatSize = (kb) => {
  if (!kb) return '';
  if (kb >= 1024*1024) return `${(kb/1024/1024).toFixed(2)} GB`;
  if (kb >= 1024) return `${(kb/1024).toFixed(2)} MB`;
  return `${kb.toFixed(0)} KB`;
};

const CATS = [
  { id:'policies',     name:'Політики' },
  { id:'certs',        name:'Сертифікати' },
  { id:'tech',         name:'Технічні документи' },
  { id:'contracts',    name:'Договори та шаблони' },
  { id:'licenses',     name:'Ліцензії та дозволи' },
  { id:'safety',       name:'Охорона праці та пожежна' },
  { id:'privacy',      name:'Конфіденційність та дані' },
  { id:'deliverypay',  name:'Оплата та доставка' },
];

const DOCS = [
  // Policies
  { cat:'policies', title:'Політика якості (ISO 9001)', file:'/docs/policy-quality-ua.pdf', type:'PDF', lang:'UA', sizeKB: 420, updated:'2025-07-02', tags:['ISO','Quality'] },
  { cat:'policies', title:'Політика охорони праці', file:'/docs/policy-hs-ua.pdf', type:'PDF', lang:'UA', sizeKB: 280, updated:'2025-05-11', tags:['H&S'] },
  // Certificates
  { cat:'certs', title:'Сертифікат ISO 9001:2015', file:'/docs/cert-iso9001.pdf', type:'PDF', lang:'EN', sizeKB: 356, updated:'2025-03-18', tags:['ISO'] },
  { cat:'certs', title:'Пожежна ліцензія (ДСНС)', file:'/docs/license-fire.pdf', type:'PDF', lang:'UA', sizeKB: 512, updated:'2025-01-22', tags:['License'] },
  // Technical
  { cat:'tech', title:'Паспорт системи відеоспостереження (типовий)', file:'/docs/spec-cctv-ua.docx', type:'DOCX', lang:'UA', sizeKB: 820, updated:'2025-06-09', tags:['CCTV','Spec'] },
  { cat:'tech', title:'Специфікація СКД для офісів', file:'/docs/spec-acs-office.xlsx', type:'XLSX', lang:'UA', sizeKB: 640, updated:'2025-05-04', tags:['ACS','BOM'] },
  { cat:'tech', title:'Робочий проєкт: вентиляція складу (зразок)', file:'/docs/project-vent-sample.pdf', type:'PDF', lang:'UA', sizeKB: 2048, updated:'2025-02-10', tags:['Vent','Project'] },
  // Contracts/Templates
  { cat:'contracts', title:'Публічний договір-оферта', file:'/docs/contract-offer.pdf', type:'PDF', lang:'UA', sizeKB: 300, updated:'2025-06-30', tags:['Legal'] },
  { cat:'contracts', title:'Договір підряду (шаблон)', file:'/docs/contract-works-template.docx', type:'DOCX', lang:'UA', sizeKB: 96, updated:'2025-06-30', tags:['Legal','Template'] },
  { cat:'contracts', title:'Акт виконаних робіт (бланк)', file:'/docs/act-completion.docx', type:'DOCX', lang:'UA', sizeKB: 52, updated:'2025-06-30', tags:['Act'] },
  // Licenses
  { cat:'licenses', title:'Дозвіл на роботи підвищеної небезпеки', file:'/docs/permit-hazard.pdf', type:'PDF', lang:'UA', sizeKB: 740, updated:'2024-12-01', tags:['Permit'] },
  // Safety
  { cat:'safety', title:'Інструкція з техніки безпеки для монтажу', file:'/docs/safety-installation.pdf', type:'PDF', lang:'UA', sizeKB: 620, updated:'2025-04-21', tags:['H&S','Installation'] },
  { cat:'safety', title:'План евакуації (приклад)', file:'/docs/evac-plan-sample.pdf', type:'PDF', lang:'UA', sizeKB: 460, updated:'2025-05-12', tags:['Fire'] },
  // Privacy
  { cat:'privacy', title:'Політика конфіденційності', file:'/docs/privacy-policy-ua.pdf', type:'PDF', lang:'UA', sizeKB: 300, updated:'2025-03-05', tags:['Privacy'] },
  { cat:'privacy', title:'Угода про нерозголошення (NDA, шаблон)', file:'/docs/nda-template.docx', type:'DOCX', lang:'UA', sizeKB: 72, updated:'2025-06-15', tags:['NDA','Template'] },
  // Delivery & Payment
  { cat:'deliverypay', title:'Оплата та умови поставки', file:'/docs/payment-delivery-ua.pdf', type:'PDF', lang:'UA', sizeKB: 380, updated:'2025-05-22', tags:['Payment','Delivery'] },
];

// простые иконки по типу
const FileIcon = ({type='PDF'}) => {
  const path = {
    PDF:  "M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 1v5h5",
    DOCX: "M4 3h10l4 4v14a2 2 0 0 1-2 2H4V3zm10 0v4h4",
    XLSX: "M4 3h16v18H4V3zm3 4l10 10M17 7L7 17",
    ZIP:  "M7 2h6v3H7V2zm2 3v3h2V5h2v14H9V5z",
    IMG:  "M4 5h16v14H4V5zm3 10l3-4 3 4 3-3 3 3"
  }[type] || "M6 2h12v20H6z";
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={path} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// =================== component ===================
export default function DocsPage(){
  const meta = usePageMeta('docs');
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [activeId, setActiveId] = useState('policies');
  const sectionsRef = useRef({});

  useEffect(()=>{ AOS.init({ once:true, duration:700, offset:80, easing:'ease-out-cubic' }); }, []);

  // IntersectionObserver для подсветки активной секции в меню
  useEffect(()=>{
    const observer = new IntersectionObserver((entries)=>{
      entries.forEach(en=>{
        if (en.isIntersecting){
          const id = en.target.getAttribute('id');
          if (id) setActiveId(id);
        }
      });
    }, { rootMargin: '-40% 0px -50% 0px', threshold: 0.01 });
    const nodes = Object.values(sectionsRef.current);
    nodes.forEach(n=>n && observer.observe(n));
    return ()=> observer.disconnect();
  }, []);

  const byCat = useMemo(()=>{
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? DOCS.filter(d =>
          d.title.toLowerCase().includes(needle) ||
          (d.tags||[]).some(tg => tg.toLowerCase().includes(needle)) ||
          d.type.toLowerCase().includes(needle) || (d.lang||'').toLowerCase().includes(needle)
        )
      : DOCS;
    const map = new Map();
    filtered.forEach(d=>{
      if (!map.has(d.cat)) map.set(d.cat, []);
      map.get(d.cat).push(d);
    });
    return map;
  }, [q]);

  const scrollTo = (id) => {
    const el = sectionsRef.current[id];
    if (el) el.scrollIntoView({ behavior:'smooth', block:'start' });
  };

  const faq = [
    { q:'Як отримати оригінали документів?', a:'Передаємо кур’єром, НП або підписуємо КЕП. Укажіть у замовленні бажаний спосіб — підготуємо пакет.' },
    { q:'Чи допомагаєте з ТЗ/проектом?', a:'Так. Є інженери по CCTV/СКД/вентиляції/електриці. Надаємо типові ТЗ, адаптуємо під об’єкт, погоджуємо з вами.' },
    { q:'Чи надаєте сертифікати на матеріали?', a:'Так, додаємо копії сертифікатів виробника/паспортів якості до акту та виконавчої документації.' },
  ];
  const [openFaq, setOpenFaq] = useState(-1);

  return (
    <Page>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/info/docs'}
      />
      <Container>
        <header style={{marginBottom:18}}>
          <div className="kicker">Документи</div>
          <h1 style={{margin:'6px 0 0'}}>Центр документів MOMV</h1>
          <p className="lead">Політики, сертифікати, зразки договорів, технічні файли для тендерів і погоджень.</p>
        </header>

        <Layout>
          {/* left nav */}
          <StickyNav>
            <NavSearch>
              <input
                type="search"
                placeholder="Пошук: назва, тег, тип, мова…"
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                aria-label="Search documents"
              />
            </NavSearch>
            <NavList>
              {CATS.map(c=>(
                <li key={c.id}>
                  <a
                    href={`#${c.id}`}
                    className={activeId===c.id ? 'active' : ''}
                    onClick={(e)=>{ e.preventDefault(); scrollTo(c.id); }}
                  >
                    <span style={{width:8, height:8, borderRadius:4, background:'var(--accent,#ff6600)'}} />
                    {c.name}
                  </a>
                </li>
              ))}
            </NavList>
          </StickyNav>

          {/* main */}
          <Main>
            {CATS.map((cat, i)=>{
              const items = byCat.get(cat.id) || [];
              return (
                <Block
                  id={cat.id}
                  key={cat.id}
                  ref={el => { sectionsRef.current[cat.id] = el; }}
                  data-aos="fade-up"
                  aria-label={cat.name}
                >
                  <SectionHead>
                    <div>
                      <div className="kicker">{cat.name}</div>
                      <h2 style={{margin:'6px 0 0'}}>{cat.name}</h2>
                    </div>
                    <small style={{opacity:.7}}>{items.length} файл(и/ів)</small>
                  </SectionHead>

                  {i===0 && (
                    <InfoNote style={{marginBottom:12}}>
                      Порада: використовуйте пошук ліворуч, щоб швидко відфільтрувати документи за тегом (наприклад, <b>ISO</b>, <b>CCTV</b>, <b>Legal</b>).
                    </InfoNote>
                  )}

                  {items.length === 0 ? (
                    <InfoNote>За запитом нічого не знайдено.</InfoNote>
                  ) : (
                    <DocGrid>
                      {items.map((d, idx)=>(
                        <Card key={d.file+idx} itemScope itemType="https://schema.org/CreativeWork">
                          <span className="stripe" aria-hidden />
                          <div className="icon" aria-hidden>
                            <FileIcon type={d.type} />
                          </div>
                          <div className="meta">
                            <h3 itemProp="name" title={d.title}>{d.title}</h3>
                            <div className="badges" aria-label="meta">
                              {d.lang && <span className="badge">{d.lang}</span>}
                              <span className="badge">{d.type}</span>
                              {d.sizeKB && <span className="badge">{formatSize(d.sizeKB)}</span>}
                              {d.updated && <span className="badge">оновлено {d.updated}</span>}
                              {(d.tags||[]).slice(0,3).map((tg,i)=><span className="badge" key={tg+i}>#{tg}</span>)}
                            </div>
                            <p itemProp="description">
                              {cat.id==='tech' && 'Технічні специфікації та зразки проектів для погодження/тендерів.'}
                              {cat.id==='contracts' && 'Юридичні шаблони для швидкого старту співпраці.'}
                              {cat.id==='certs' && 'Підтвердження відповідності стандартам і ліцензійні документи.'}
                              {cat.id==='policies' && 'Внутрішні політики й принципи роботи компанії.'}
                              {cat.id==='safety' && 'Інструкції, регламенти та плани безпеки.'}
                              {cat.id==='privacy' && 'Документи щодо конфіденційності та роботи з даними.'}
                              {cat.id==='deliverypay' && 'Умови оплати, постачання та повернень.'}
                            </p>
                            <div className="actions">
                              <a className="btn btn--primary" href={d.file} download>
                                Завантажити
                              </a>
                              <a className="btn" href={d.file} target="_blank" rel="noopener noreferrer">
                                Переглянути
                              </a>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </DocGrid>
                  )}
                </Block>
              );
            })}

            {/* Доп. секція: Питання та допомога */}
            <Block data-aos="fade-up">
              <SectionHead>
                <div>
                  <div className="kicker">Допомога</div>
                  <h2 style={{margin:'6px 0 0'}}>Питання та підтримка</h2>
                </div>
              </SectionHead>

              <Accordion>
                {faq.map((it, idx)=>(
                  <div key={idx}>
                    <button className="q" aria-expanded={idx===openFaq} onClick={()=>setOpenFaq(idx===openFaq?-1:idx)}>
                      <span>{it.q}</span>
                      <span>{idx===openFaq ? '−' : '+'}</span>
                    </button>
                    {idx===openFaq && <div className="a">{it.a}</div>}
                  </div>
                ))}
              </Accordion>

              <InfoNote style={{marginTop:12}}>
                Потрібен інший документ (виконавча, листи-відгуки, довідки)? Напишіть нам на <a href="mailto:info@momv.com.ua">info@momv.com.ua</a> —
                підготуємо індивідуальний пакет під ваш тендер/аудит.
              </InfoNote>
            </Block>

            {/* Примітка про актуальність */}
            <InfoNote>
              Остання ревізія розділу документів: <b>серпень 2025</b>. Ми регулярно оновлюємо файли після змін у законодавстві та стандартах.
            </InfoNote>
          </Main>
        </Layout>
      </Container>
    </Page>
  );
}
